import { DEFAULT_GPT_IMAGE_MODEL } from '@/constants/gptImageModels';
import useLocale from 'next-translate/useTranslation';
import React, { FC, useState, useMemo, memo } from 'react';
import { ImageModelType, TrainingModelType } from 'types';
import {
  modelsWithDescription,
  getModelDisplayName,
} from '@/constants/imageModels';
import {
  ChevronDownIcon,
  SparklesIcon,
  StarIcon,
} from '@heroicons/react/solid';
import { ModelSelectorModal } from './ModelSelectorModal';

interface ModelSelectorButtonProps {
  value?: ImageModelType | ImageModelType[];
  onChange: (value: ImageModelType | ImageModelType[]) => void;
  trainingModels?: TrainingModelType[];
  defaultTrainingModel?: TrainingModelType;
}

const ModelSelectorButton: FC<ModelSelectorButtonProps> = ({
  value,
  onChange,
  trainingModels = [],
  defaultTrainingModel,
}) => {
  const { t } = useLocale('ai-images');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get current selected model(s) info
  const selectedModels = useMemo(() => {
    const models = Array.isArray(value)
      ? value
      : [value || defaultTrainingModel?.name || DEFAULT_GPT_IMAGE_MODEL];
    return models;
  }, [value, defaultTrainingModel]);

  // Get display info for button
  const displayInfo = useMemo(() => {
    if (selectedModels.length === 1) {
      const modelId = selectedModels[0];

      // Check if it's a custom model
      const customModel = trainingModels.find((m) => m.name === modelId);
      if (customModel) {
        return {
          title: customModel.name || '',
          description: 'Custom trained model',
          credits: '',
          isCustom: true,
          icon: null,
          icons: [],
          isMultiple: false,
        };
      }

      // Find in standard models
      const standardModel = modelsWithDescription.find(
        (m) => m.title === modelId || m.id === modelId,
      );

      if (standardModel) {
        return {
          title: getModelDisplayName(standardModel.title),
          description: standardModel.description,
          credits: standardModel.users,
          isCustom: false,
          icon: standardModel.icon,
          icons: [],
          isMultiple: false,
        };
      }

      // Default fallback
      return {
        title: getModelDisplayName(modelId),
        description: '',
        credits: '',
        isCustom: false,
        icons: [],
        isMultiple: false,
      };
    }

    // Multiple models selected - get all icons
    const modelIcons = selectedModels
      .map((modelName) => {
        const model = modelsWithDescription.find(
          (m) => m.title === modelName || m.id === modelName,
        );
        return model?.icon || null;
      })
      .filter((icon) => icon !== null);

    return {
      title: `${selectedModels.length} Models Selected`,
      description: selectedModels.map(getModelDisplayName).join(', '),
      credits: '',
      isCustom: false,
      icon: null,
      icons: modelIcons,
      isMultiple: true,
    };
  }, [selectedModels, trainingModels]);

  // Extract credit number for badge
  const getCreditBadge = (creditText: string) => {
    if (!creditText) return null;
    if (creditText.toLowerCase().includes('free')) {
      return (
        <span className='px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'>
          Free
        </span>
      );
    }

    const match = creditText.match(/(\d+(?:\.\d+)?)\s*credit/);
    if (match) {
      const credits = parseFloat(match[1]);
      const badgeColor =
        credits >= 3
          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
          : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      return (
        <span
          className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${badgeColor}`}
        >
          {credits} {credits === 1 ? 'credit' : 'credits'}
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <button
        type='button'
        onClick={() => setIsModalOpen(true)}
        className='w-full px-4 py-3 text-start bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600
                   rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2
                   focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200'
      >
        <div className='flex items-center justify-between'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 mb-1'>
              {displayInfo.isCustom && (
                <StarIcon className='w-4 h-4 text-blue-500 flex-shrink-0' />
              )}

              {/* Show all model icons for multiple selection */}
              {displayInfo.isMultiple &&
              displayInfo.icons &&
              displayInfo.icons.length > 0 ? (
                <div className='flex items-center gap-1 flex-shrink-0'>
                  {displayInfo.icons.slice(0, 5).map((Icon, index) => (
                    <div key={index} className='flex-shrink-0'>
                      {Icon &&
                        React.createElement(Icon, {
                          className: 'w-4 h-4',
                        })}
                    </div>
                  ))}
                  {displayInfo.icons.length > 5 && (
                    <span className='text-xs text-gray-500 dark:text-gray-400'>
                      +{displayInfo.icons.length - 5}
                    </span>
                  )}
                </div>
              ) : null}

              <span className='text-sm flex items-center font-medium text-gray-900 dark:text-gray-100 truncate'>
                {displayInfo.icon &&
                  React.createElement(displayInfo.icon, {
                    className: 'w-4 h-4 me-2 flex-shrink-0',
                  })}
                <span translate='no'>{displayInfo.title}</span>
              </span>
              {/* {getCreditBadge(displayInfo.credits)} */}
            </div>
            {displayInfo.description && (
              // Descriptions are English: their own direction, so a long one
              // is cut at its end, but aligned with the title in RTL.
              <p
                className='text-xs text-gray-500 dark:text-gray-400 truncate rtl:text-right'
                dir='auto'
              >
                {displayInfo.description}
              </p>
            )}
          </div>
          <ChevronDownIcon className='w-5 h-5 text-gray-400 flex-shrink-0 ms-2' />
        </div>
      </button>

      <ModelSelectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        value={value}
        onChange={onChange}
        trainingModels={trainingModels}
        defaultTrainingModel={defaultTrainingModel}
      />
    </>
  );
};

//memoized export
export default memo(ModelSelectorButton);
